import { useEffect, useMemo, useState } from "react";
import { Space, Tag } from "antd";
import type { PropRuntimeGraph } from "../../game/assets/shared/types/PropRuntimeGraph";
import type { BattleManager } from "../../game/assets/pri_wqs/scripts/core/BattleManager";
import type {
  PropRuntimeEvent,
  RuntimePropInstance,
} from "../../game/assets/pri_wqs/scripts/core/propMechanics/PropMechanicsTypes";
import type { SandboxCellSelection } from "./types";
import {
  BoardCell,
  BoardGrid,
  SandboxSection,
  SandboxSectionHeader,
} from "./styles";
import {
  getRuntimeInstanceName,
  isRuntimeEmptySlot,
} from "./runtimeBridge";
import { PropCard } from "./PropCard";

const BOARD_ROWS = 4;
const BOARD_COLS = 5;
const FIRST_ROW_INDEX = 1;
const FIRST_COLUMN_INDEX = 0;

type BoardMatrix = Array<Array<RuntimePropInstance | undefined>>;
type TimerMap = Record<string, number | undefined>;

export interface BoardPanelProps {
  manager?: BattleManager;
  catalog?: PropRuntimeGraph[];
  selectedCell?: SandboxCellSelection;
  selectedInstanceId: string;
  onSelectCell: (cell: SandboxCellSelection) => void;
  onSelectInstance: (item?: RuntimePropInstance) => void;
}

export function BoardPanel({
  manager,
  catalog,
  selectedCell,
  selectedInstanceId,
  onSelectCell,
  onSelectInstance,
}: BoardPanelProps) {
  const [boardMatrix, setBoardMatrix] = useState<BoardMatrix>(() => createEmptyBoardMatrix());
  const [timers, setTimers] = useState<TimerMap>({});

  useEffect(() => {
    setBoardMatrix(createEmptyBoardMatrix());
    setTimers({});
    if (!manager) return undefined;

    const eventBus = manager.getEventBus();
    const enterBoard = (event: PropRuntimeEvent) => {
      if (!event.item) return;
      const cell = readEventCell(event);
      if (!cell) return;
      setBoardMatrix((previous) => setBoardItem(previous, cell.row, cell.col, event.item));
    };
    const leaveBoard = (event: PropRuntimeEvent) => {
      const cell = readEventCell(event);
      if (!cell) return;
      setBoardMatrix((previous) => setBoardItem(previous, cell.row, cell.col, undefined));
    };
    const timerChanged = (event: PropRuntimeEvent) => {
      if (!event.item) return;
      setTimers((previous) => setTimerValue(previous, event.item!.id, event.timerValue));
    };

    const unsubs = [
      eventBus.on("propEnteredBoard", enterBoard),
      eventBus.on("propLeftBoard", leaveBoard),
      eventBus.on("propTimerChanged", timerChanged),
    ];

    return () => {
      unsubs.forEach((unsubscribe) => unsubscribe());
    };
  }, [manager]);

  const selectedInstance = useMemo(() => {
    if (!selectedInstanceId) return undefined;
    return boardMatrix.flat().find((item) => item?.id === selectedInstanceId);
  }, [boardMatrix, selectedInstanceId]);

  return (
    <SandboxSection $fill>
      <SandboxSectionHeader>
        <span>盘面</span>
        <Space size={4}>
          {selectedInstance && <Tag color="green">{getRuntimeInstanceName(selectedInstance)}</Tag>}
        </Space>
      </SandboxSectionHeader>
      <BoardGrid $rows={BOARD_ROWS} $cols={BOARD_COLS}>
        {Array.from({ length: BOARD_ROWS }, (_, rowIndex) => (
          Array.from({ length: BOARD_COLS }, (_, colIndex) => {
            const item = boardMatrix[rowIndex]?.[colIndex];
            const rowValue = FIRST_ROW_INDEX + rowIndex;
            const colValue = FIRST_COLUMN_INDEX + colIndex;
            const selected = selectedCell?.row === rowValue && selectedCell?.col === colValue;
            const empty = isRuntimeEmptySlot(item);
            return (
              <BoardCell
                key={`${rowValue}_${colValue}`}
                $selected={selected || item?.id === selectedInstanceId}
                $empty={empty}
                $rarity={item?.graph.rarity}
                onClick={() => {
                  onSelectCell({ row: rowValue, col: colValue });
                  onSelectInstance(item);
                }}
              >
                <PropCard
                  item={item}
                  catalog={catalog}
                  empty={empty || !item}
                  imageSize={36}
                  timerValue={item ? timers[item.id] : undefined}
                  enableMagicEffectAnchor
                />
              </BoardCell>
            );
          })
        ))}
      </BoardGrid>
    </SandboxSection>
  );
}

function setTimerValue(previous: TimerMap, id: string, value: number | undefined): TimerMap {
  const next = { ...previous };
  if (typeof value === "number") {
    next[id] = value;
  } else {
    delete next[id];
  }
  return next;
}

function createEmptyBoardMatrix(): BoardMatrix {
  return Array.from({ length: BOARD_ROWS }, () => Array.from({ length: BOARD_COLS }, () => undefined));
}

function readEventCell(event: PropRuntimeEvent): SandboxCellSelection | undefined {
  if (Number.isFinite(event.row) && Number.isFinite(event.col)) {
    return { row: Number(event.row), col: Number(event.col) };
  }
  if (event.type === "propEnteredBoard" && event.item && event.item.row >= 0 && event.item.col >= 0) {
    return { row: event.item.row, col: event.item.col };
  }
  return undefined;
}

function setBoardItem(
  previous: BoardMatrix,
  row: number,
  col: number,
  item: RuntimePropInstance | undefined,
): BoardMatrix {
  const rowIndex = row - FIRST_ROW_INDEX;
  const colIndex = col - FIRST_COLUMN_INDEX;
  if (rowIndex < 0 || rowIndex >= BOARD_ROWS || colIndex < 0 || colIndex >= BOARD_COLS) return previous;

  const next = previous.map((line) => [...line]);
  next[rowIndex][colIndex] = item;
  return next;
}
